import * as Qualified from '@packages/systems/wfdl-Qualified';

/**
 * A dependency required by site modules.
 *
 * @example
 * ```typescript
 * {
 *   version: '^4.17.21',
 *   integrity: 'sha384-oqVuAfXRKap7fdgcCY5uykM6+R9GqQ8K/uxy9rx7HNQlGYl1kPzQho1wx4JwY8wC'
 * }
 * ```
 */
export type SiteModuleDependencyType = {
  /**
   * Semantic version or version range (npm-compatible).
   * Examples: '1.0.0', '^1.0.0', '~1.0.0', '>=1.0.0 <2.0.0'
   */
  version: string;

  /**
   * Optional subresource integrity hash for security verification.
   * Format: algorithm-hash (e.g., 'sha384-...' or 'sha512-...')
   *
   * Supported algorithms: sha256, sha384, sha512
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Security/Subresource_Integrity
   */
  integrity?: string;

  /**
   * Optional list of exports to allow importing from this package, e.g.
   * ['jsx-runtime']
   */
  exports?: Array<string>;
};

export type PersistedSiteModules = Array<{
  name: string;
  version: string;
  exports?: Array<string>;
  integrity?: string;
}>;

/**
 * Map of npm package names to their dependency specifications.
 *
 * @example
 * ```typescript
 * {
 *   'lodash': {
 *     version: '4.17.21',
 *     integrity: 'sha384-...'
 *   },
 *   'gsap': {
 *     version: '^3.12.0'
 *   }
 * }
 * ```
 */
export type SiteModuleDependencyMap = {
  [packageName: string]: SiteModuleDependencyType;
};

export const dependencyName = Qualified.siteModuleDependencyName;

export const createDependency = (
  dependency: SiteModuleDependencyType
): SiteModuleDependencyType => dependency;

export const parse = (dependenciesArray: PersistedSiteModules) =>
  dependenciesArray.reduce((acc: SiteModuleDependencyMap, dep) => {
    acc[dep.name] = {
      version: dep.version,
      ...(dep.integrity ? {integrity: dep.integrity} : {}),
      ...(dep.exports ? {exports: dep.exports} : {}),
    };
    return acc;
  }, {});

/**
 * Convert siteModuleDependencies from map format to array format for the API.
 */
export function serialize(
  dependencies: SiteModuleDependencyMap
): Array<PersistedSiteModules[0]> {
  return Object.entries(dependencies).map(([name, dep]) => ({
    name,
    version: dep.version,
    ...(dep.integrity ? {integrity: dep.integrity} : {}),
    ...(dep.exports ? {exports: dep.exports} : {}),
  }));
}
